import { hueToCss } from '../utils/color.js';
import { fplTrackBorderColorVar, fplTrackBgVar, fplThumbBgVar, sliderHeight } from '../slider/slider.css.js';

/**
 * The gradient stops used to draw the hue wheel. The sRGB hue wheel is piecewise-linear with a
 * vertex every 60deg (the six corners of the RGB cube), so 7 stops — one on each vertex,
 * including red at both 0deg and 360deg — reproduce it exactly. More stops are redundant; a
 * count that isn't `6k + 1` misses the vertices and rounds the corners.
 */
const NUM_STOPS = 7;
/**
 * The lighter translucent border token, referenced as a raw CSS var rather than importing the
 * `@figma/fpl-tokens` map — this is runtime code, so the import would bundle the whole map.
 */
const BORDER_COLOR = 'var(--color-bordertranslucent)';
/**
 * Build the `Slider` CSS variables for a hue slider: a thumb painted with the fully-saturated
 * hue at `hue`, and a track showing the hue wheel as a horizontal gradient.
 */
function makeHueSliderStyle(hue, colorSpace) {
    return {
        [fplThumbBgVar]: hueToCss(hue, colorSpace),
        [fplTrackBgVar]: makeHueGradient(colorSpace),
        [fplTrackBorderColorVar]: BORDER_COLOR,
    };
}
const HUE_GRADIENT_CACHE = new Map();
function makeHueGradient(colorSpace) {
    let gradient = HUE_GRADIENT_CACHE.get(colorSpace);
    if (!gradient) {
        // Inset the first and last stops by half the track height so the gradient lines up with the
        // center of the thumb at the ends of its travel, rather than the edge of the track.
        const padding = `calc(${sliderHeight} / 2)`;
        const stops = [];
        for (let i = 0; i < NUM_STOPS; ++i) {
            // Divide by NUM_STOPS - 1 (not NUM_STOPS) so the last stop lands on hue 1 (red again),
            // completing the wheel. With NUM_STOPS the track would stop ~10deg short at the high end and
            // the thumb's color wouldn't match the gradient beneath it.
            const color = hueToCss(i / (NUM_STOPS - 1), colorSpace);
            if (i === 0) {
                stops.push(`${color} ${padding}`);
            }
            else if (i === NUM_STOPS - 1) {
                stops.push(`${color} calc(100% - ${padding})`);
            }
            else {
                stops.push(color);
            }
        }
        gradient = `linear-gradient(90deg, ${stops.join(', ')})`;
        HUE_GRADIENT_CACHE.set(colorSpace, gradient);
    }
    return gradient;
}

export { makeHueSliderStyle };
//# sourceMappingURL=hue-slider.utils.js.map
